/**
 * Proof of control — Phase 4.5.
 *
 * Everything before this phase proves *process*: who approved which bytes, tamper-evidently.
 * That is a real property and it is not the one an auditor, an insurer or a Travel Rule
 * counterparty asks about. They ask **"prove you control these keys."** This file is the
 * answer, in two artifacts that deliberately stay separate because they answer different
 * questions:
 *
 *   · An **approval attestation** says *"I, the holder of key X, reviewed this exact intent."*
 *     It binds a human decision to a specific payload and a specific key.
 *   · A **control proof** says *"this vault controls this address."* An external party issues a
 *     challenge, `m` of `n` signers sign it, and anyone can check it.
 *
 * ── THE RULE THAT SHAPES EVERY FUNCTION HERE ────────────────────────────────
 *
 * **The message is RECOMPUTED from the record, never read out of the artifact.**
 *
 * The tempting design is to carry the signed message as a string in the bundle and check the
 * signature against it. That verifies that somebody signed some text. It does not verify that
 * the text is the approval, the challenge, or anything at all — an attacker supplies both the
 * message and the signature and every check passes. The same failure mode, in a different
 * costume, is the `NETWORK_MISMATCH` bug this codebase already shipped once: a comparison whose
 * two sides came from a single source, sitting green forever.
 *
 * So every verifier here rebuilds the message from typed fields via a constructor that
 * hard-codes the `type` tag, and there is nowhere to pass a message in.
 *
 * The second half of the same rule: **the pubkey that signed is recovered from the signature,
 * and the pubkey that SHOULD have signed is derived from the descriptor.** Two independent
 * sources. A signature from a key outside the descriptor cannot match one derived from it, so
 * "refused rather than counted" is structural rather than a check somebody remembered to write.
 *
 * ── WHAT THIS FILE DOES NOT DO ──────────────────────────────────────────────
 *
 * It does not sign. `packages/core` never holds key material; signing happens on a device or in
 * a wallet, and the only thing that comes back here is a signature to check. The one exception
 * is `signMessageForTest`, which exists for the test suite and says so in its name.
 *
 * It does not claim BIP-322 multisig works on any particular device. Support is thin and
 * unverified, and BIP-322 is NOT BUILT here: which devices can sign a message under a multisig
 * policy is an open question that only a real device answers, so `bip322-simple` is refused
 * with SCHEME_UNSUPPORTED. `bip137` is the widely-supported path and is the default.
 */
import { secp256k1 } from '@noble/curves/secp256k1.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes, utf8ToBytes, concatBytes } from '@noble/hashes/utils.js';
import { base64 } from '@scure/base';
import { jcs } from './intent.js';
import { parseDescriptor, resolveClass } from './descriptors.js';
export class AttestError extends Error {
    code;
    constructor(code, message) {
        super(`${code}: ${message}`);
        this.code = code;
        this.name = 'AttestError';
    }
}
/* ── the records that get signed ──────────────────────────────────────────── */
/**
 * `type` is inside the signed bytes, and each constructor below hard-codes its own value. That
 * is the domain separation: a signature over an approval can never be replayed as a challenge
 * response, because the verifier for a challenge only ever builds a challenge message.
 */
export const APPROVAL_TYPE = 'signingroom.approval.v1';
export const CHALLENGE_TYPE = 'signingroom.control-challenge.v1';
/**
 * Build the exact bytes a signer signs for an approval.
 *
 * Exported because a third-party verifier in another language must be able to reproduce it, and
 * because `sim/` re-derives it independently. `jcs` sorts keys and emits bigints as decimal
 * strings, so the bytes are identical in every language — see `intent.ts`.
 */
export function approvalMessage(r) {
    assertRecord(r);
    return jcs({
        type: APPROVAL_TYPE,
        approvedAt: r.approvedAt,
        decision: r.decision,
        descriptorChecksum: r.descriptorChecksum,
        intentHash: r.intentHash,
        payloadHash: r.payloadHash,
        roomId: r.roomId,
        vaultId: r.vaultId,
    });
}
/** Build the exact bytes a signer signs for a control challenge. */
export function challengeMessage(c) {
    assertChallenge(c);
    return jcs({
        type: CHALLENGE_TYPE,
        challenge: c.challenge,
        descriptorChecksum: c.descriptorChecksum,
        expiresAt: c.expiresAt,
        issuedAt: c.issuedAt,
        nonce: c.nonce,
        vaultId: c.vaultId,
    });
}
const HEX64 = /^[0-9a-f]{64}$/;
/**
 * A BIP-380 descriptor checksum is 8 characters of the BECH32 charset, not hex.
 *
 * This was `/^[0-9a-f]{8}$/` for one build and the attestation tests refused every real
 * descriptor in the repo — the fixture vault's checksum contains a `t`. Worth keeping the note:
 * a validator stricter than the format rejects valid input, and the failure surfaces as
 * "your descriptor is malformed" pointed at a descriptor that is fine.
 */
const CHECKSUM8 = /^[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{8}$/;
/**
 * Fail closed on a malformed record rather than hashing it anyway.
 *
 * A record with an undefined field would serialise without that key and produce a hash that
 * looks fine — a commitment to less than the caller believes. Two records differing only in a
 * field one of them omits would then be indistinguishable to a verifier.
 */
function assertRecord(r) {
    const bad = (f, why) => {
        throw new AttestError('APPROVAL_RECORD_INVALID', `${f} ${why}`);
    };
    if (typeof r.vaultId !== 'string' || r.vaultId === '')
        bad('vaultId', 'is missing');
    if (typeof r.roomId !== 'string' || r.roomId === '')
        bad('roomId', 'is missing');
    if (!CHECKSUM8.test(r.descriptorChecksum ?? ''))
        bad('descriptorChecksum', 'is not an 8-character BIP-380 checksum');
    if (!HEX64.test(r.payloadHash ?? ''))
        bad('payloadHash', 'is not a sha256 hex digest');
    if (!HEX64.test(r.intentHash ?? ''))
        bad('intentHash', 'is not a sha256 hex digest');
    if (r.decision !== 'APPROVE' && r.decision !== 'REJECT')
        bad('decision', 'must be APPROVE or REJECT');
    if (!Number.isSafeInteger(r.approvedAt) || r.approvedAt <= 0)
        bad('approvedAt', 'is not a positive integer millisecond timestamp');
}
function assertChallenge(c) {
    const bad = (f, why) => {
        throw new AttestError('CHALLENGE_INVALID', `${f} ${why}`);
    };
    if (typeof c.vaultId !== 'string' || c.vaultId === '')
        bad('vaultId', 'is missing');
    if (!CHECKSUM8.test(c.descriptorChecksum ?? ''))
        bad('descriptorChecksum', 'is not an 8-character BIP-380 checksum');
    if (typeof c.challenge !== 'string' || c.challenge === '')
        bad('challenge', 'is empty');
    // A short nonce is a replayable nonce. 16 bytes is the floor, not a suggestion.
    if (typeof c.nonce !== 'string' || !/^[0-9a-f]{32,}$/.test(c.nonce))
        bad('nonce', 'must be at least 16 bytes of lowercase hex');
    if (!Number.isSafeInteger(c.issuedAt) || c.issuedAt <= 0)
        bad('issuedAt', 'is not a positive integer millisecond timestamp');
    if (!Number.isSafeInteger(c.expiresAt) || c.expiresAt <= c.issuedAt)
        bad('expiresAt', 'does not come after issuedAt');
}
/** The BIP-137 preimage: a length-prefixed magic string, then the length-prefixed message. */
function bip137Hash(message) {
    const magic = utf8ToBytes('Bitcoin Signed Message:\n');
    const msg = utf8ToBytes(message);
    return sha256(sha256(concatBytes(varString(magic), varString(msg))));
}
/**
 * Bitcoin's CompactSize length prefix.
 *
 * A message longer than 252 bytes needs a multi-byte prefix, and every approval record is
 * longer than that — so a naive single-byte implementation would work on toy inputs and
 * produce a wrong hash on every real one.
 */
function varString(b) {
    const n = b.length;
    let prefix;
    if (n < 0xfd)
        prefix = Uint8Array.of(n);
    else if (n <= 0xffff)
        prefix = Uint8Array.of(0xfd, n & 0xff, (n >> 8) & 0xff);
    else if (n <= 0xffffffff)
        prefix = Uint8Array.of(0xfe, n & 0xff, (n >> 8) & 0xff, (n >> 16) & 0xff, (n >>> 24) & 0xff);
    else
        throw new AttestError('MESSAGE_TOO_LONG', `${n} bytes`);
    return concatBytes(prefix, b);
}
/**
 * Recover the compressed public key that produced a BIP-137 signature.
 *
 * Returns null rather than throwing on anything malformed: a bad signature in a bundle is an
 * ordinary rejection to be reported next to the others, not an exception that abandons the
 * whole verification and hides the signatures that were fine.
 */
export function recoverBip137(message, signatureB64) {
    let raw;
    try {
        raw = base64.decode(signatureB64);
    }
    catch {
        return null;
    }
    if (raw.length !== 65)
        return null;
    /*
     * Header encodes the recovery id AND the address type the signer had in mind:
     *   27..30 uncompressed, 31..34 compressed, 35..38 p2sh-p2wpkh, 39..42 p2wpkh.
     * Anything outside 27..42 is not a BIP-137 signature.
     *
     * ⚠️ THE ADDRESS-TYPE BAND IS DELIBERATELY NOT ENFORCED, which makes a signature MALLEABLE in
     * its first byte: 31 and 35 differ only in the band, carry the same recovery id under
     * `(header - 27) & 3`, and therefore recover the same public key. So the same proof can be
     * re-encoded into a different blob that verifies identically.
     *
     * That is not forgery and it is not a hole — the claim being checked is "this KEY signed this
     * message", settled by comparing the recovered pubkey against the keys the descriptor derives,
     * and the band says nothing about that. Enforcing it would instead reject honest signers:
     * wallets disagree about which band to emit for a segwit address, and this codebase's own
     * `signMessageForTest` emits the 31 band for a `wpkh` descriptor whose "correct" band is 39.
     *
     * The consequence that DOES matter: a control proof is not byte-canonical, so two bundles that
     * prove the same thing can differ. Never deduplicate, hash or compare proofs by their bytes —
     * compare the recovered key and the message. Pinned by a test, so this stays a known property
     * rather than a surprise found by flipping a character.
     */
    const header = raw[0];
    if (header < 27 || header > 42)
        return null;
    const recovery = (header - 27) & 3;
    // A vault key is always compressed. An uncompressed header would recover a different pubkey
    // encoding than the descriptor derives, so it cannot match — reject it by name instead of
    // letting it fail later as a mysterious mismatch.
    if (header < 31)
        return null;
    try {
        const compact = raw.slice(1);
        const recovered = secp256k1.recoverPublicKey(concatBytes(Uint8Array.of(recovery), compact), bip137Hash(message), { prehash: false });
        return recovered.length === 33 ? recovered : null;
    }
    catch {
        return null;
    }
}
/**
 * FOR TESTS ONLY — this package never holds key material in production.
 *
 * Named so that an import of it in application code is visible in review and in a grep, which
 * is the point. `test/zz-audit-core.test.ts` asserts no `src/` file outside this one references
 * a private key.
 */
export function signMessageForTest(message, privateKey) {
    const sig = secp256k1.sign(bip137Hash(message), privateKey, { prehash: false, format: 'recovered' });
    // noble emits [recovery, r, s]; BIP-137 wants [header, r, s] with the compressed flag set.
    return base64.encode(concatBytes(Uint8Array.of(31 + sig[0]), sig.slice(1)));
}
/**
 * Check one signature against the descriptor.
 *
 * The whole security argument is in four lines below: derive what SHOULD have signed from the
 * descriptor, recover what DID sign from the signature, compare. Neither side can be supplied
 * by whoever built the bundle.
 */
function verifyOne(d, message, s) {
    const no = (code, reason) => ({ no: { keyIndex: s.keyIndex, code, reason } });
    if (!Number.isInteger(s.keyIndex) || s.keyIndex < 0 || s.keyIndex >= d.keys.length)
        return no('KEY_INDEX_OUT_OF_RANGE', `slot ${s.keyIndex} does not exist in a ${d.n}-key descriptor`);
    if (!Number.isInteger(s.branch) || s.branch < 0 || !Number.isInteger(s.index) || s.index < 0)
        return no('DERIVATION_INVALID', 'branch and index must be non-negative integers');
    if (s.scheme !== 'bip137')
        return no('SCHEME_UNSUPPORTED', `${s.scheme} is not implemented; only bip137 (Bitcoin Signed Message) can be verified here.`);
    let expected;
    try {
        expected = resolveClass(d.payload).derive(d, s.branch, s.index).pubkeys[s.keyIndex];
    }
    catch (e) {
        return no('DERIVE_FAILED', e.message);
    }
    const recovered = recoverBip137(message, s.signature);
    if (!recovered)
        return no('SIGNATURE_MALFORMED', 'not a well-formed compressed BIP-137 signature');
    // The two sides come from genuinely different places: `expected` from the descriptor's xpubs,
    // `recovered` from the signature bytes. A key outside the descriptor cannot produce a match.
    if (bytesToHex(recovered) !== bytesToHex(expected))
        return no('KEY_NOT_IN_DESCRIPTOR', `signature was made by ${bytesToHex(recovered).slice(0, 16)}…, which is not the key at ` +
            `slot ${s.keyIndex} path ${s.branch}/${s.index}`);
    return { ok: {
            keyIndex: s.keyIndex, branch: s.branch, index: s.index, scheme: s.scheme,
            fingerprint: d.keys[s.keyIndex].origin.fingerprint, pubkey: bytesToHex(recovered),
        } };
}
/**
 * Verify that the holder of a vault key attested to this exact approval.
 *
 * `descriptor` is supplied by the RELYING PARTY, not by the attestation — it is the thing they
 * already know about the vault. An attestation that carried its own descriptor would let a
 * forger supply a descriptor made of their own keys and pass every check.
 */
export function verifyApproval(descriptor, a) {
    const d = parseDescriptor(descriptor);
    if (a.record.descriptorChecksum !== d.checksum)
        return { ok: false, signer: null, rejected: { keyIndex: a.signature?.keyIndex ?? -1,
                code: 'DESCRIPTOR_MISMATCH',
                reason: `record names descriptor ${a.record.descriptorChecksum}, verifying against ${d.checksum}` } };
    const r = verifyOne(d, approvalMessage(a.record), a.signature);
    return 'ok' in r
        ? { ok: true, signer: r.ok, rejected: null }
        : { ok: false, signer: null, rejected: r.no };
}
/**
 * Verify a control proof.
 *
 * `now` is a parameter, not a `Date.now()` call, so expiry is testable and so a verifier in a
 * different timezone or a replayed transcript produces the same answer as everyone else.
 *
 * The proof is bound at four independent points, and defeating it requires all four:
 *   1. the signed bytes contain the challenger's nonce, so an old proof does not answer a new
 *      challenge;
 *   2. the signed bytes contain the descriptor checksum, which is compared against the checksum
 *      of the descriptor actually supplied — a bundle cannot be pointed at a different vault;
 *   3. each signature must recover to a key the descriptor derives;
 *   4. `m` DISTINCT slots must be satisfied, so one key signing `m` times proves nothing.
 */
export function verifyControlProof(p, now) {
    const fail = (failure, partial = {}) => ({
        ok: false, address: null, m: 0, n: 0, satisfied: 0, valid: [], rejected: [], failure,
        ...partial,
    });
    let d;
    try {
        d = parseDescriptor(p.descriptor);
    }
    catch (e) {
        return fail(`descriptor did not parse: ${e.message}`);
    }
    let message;
    try {
        message = challengeMessage(p.challenge);
    }
    catch (e) {
        return fail(e.message, { m: d.m, n: d.n });
    }
    const base = { m: d.m, n: d.n };
    // The challenge names a descriptor; this is the one we were handed. Two sources, so this
    // comparison can actually fail — unlike one that reads both sides out of the same object.
    if (p.challenge.descriptorChecksum !== d.checksum)
        return fail(`the challenge was issued for descriptor ${p.challenge.descriptorChecksum}, but this ` +
            `bundle carries ${d.checksum}`, base);
    if (now >= p.challenge.expiresAt)
        return fail(`the challenge expired at ${p.challenge.expiresAt} and it is now ${now}`, base);
    if (now < p.challenge.issuedAt)
        return fail(`the challenge is not valid until ${p.challenge.issuedAt}`, base);
    if (!Array.isArray(p.signatures) || p.signatures.length === 0)
        return fail('the bundle carries no signatures', base);
    const valid = [];
    const rejected = [];
    const seenSlots = new Set();
    for (const s of p.signatures) {
        const r = verifyOne(d, message, s);
        if ('no' in r) {
            rejected.push(r.no);
            continue;
        }
        // One key signing m times is one key. Count the SLOT, not the signature.
        if (seenSlots.has(r.ok.keyIndex)) {
            rejected.push({ keyIndex: r.ok.keyIndex, code: 'DUPLICATE_SLOT',
                reason: `slot ${r.ok.keyIndex} already signed; a repeat does not advance the threshold` });
            continue;
        }
        seenSlots.add(r.ok.keyIndex);
        valid.push(r.ok);
    }
    // Every signature must be at the SAME derivation, or the bundle proves "these keys exist"
    // rather than "this vault controls this address" — which is the claim being made.
    const spots = new Set(valid.map((v) => `${v.branch}/${v.index}`));
    if (spots.size > 1)
        return fail(`signatures are spread across ${spots.size} derivations (${[...spots].join(', ')}); a ` +
            `control proof must name one address`, { ...base, valid, rejected, satisfied: valid.length });
    let address = null;
    if (valid.length > 0) {
        try {
            address = resolveClass(d.payload).derive(d, valid[0].branch, valid[0].index).address;
        }
        catch {
            address = null;
        }
    }
    const satisfied = valid.length;
    if (satisfied < d.m)
        return fail(`${satisfied} of the required ${d.m} keys signed`, { ...base, valid, rejected, satisfied, address });
    return { ok: true, address, m: d.m, n: d.n, satisfied, valid, rejected, failure: null };
}
/** Hex helper for callers building a challenge. Not random — the CHALLENGER supplies entropy. */
export const nonceFromBytes = (b) => bytesToHex(b);
export { hexToBytes };
//# sourceMappingURL=attest.js.map